<script setup lang="ts">
import {
  jobList,
  areaList,
  genderList,
  contractorList,
} from "~/constants/recruit";

interface Props {
  type: "newgrad" | "career" | "contractor";
}
defineProps<Props>();
const { recruitData, isAgree } = useRecruit();
</script>
<template>
  <div class="p-recruit__form">
    <div class="p-recruit__form--item">
      <p class="item-name">
        職種選択
        <span class="required">必須</span>
        <span
          v-if="type == 'contractor'"
          class="text-[#c62a2a] text-sm w-full mt-1"
          >※複数選択可</span
        >
      </p>
      <div class="p-4">
        <FormMultipleSelect
          v-if="type == 'contractor'"
          :required="true"
          :list="contractorList"
          v-model="recruitData.work"
          >対応できる職種を選択してください</FormMultipleSelect
        >
        <FormRadioSelect
          v-if="type == 'newgrad' || type == 'career'"
          class="grid grid-cols-1 tablet:grid-cols-2 gap-2"
          :required="true"
          v-model="recruitData.job"
          :list="jobList"
          name="job"
        />
      </div>
    </div>
    <div
      v-if="type == 'newgrad' || type == 'career'"
      class="p-recruit__form--item"
    >
      <p class="item-name">
        希望勤務地
        <span class="required">必須</span>
        <span class="text-[#c62a2a] text-sm w-full mt-1">※複数選択可</span>
      </p>
      <div class="p-4">
        <FormMultipleSelect
          :required="true"
          :list="areaList"
          v-model="recruitData.area"
          >希望する勤務地を選択してください</FormMultipleSelect
        >
      </div>
    </div>
    <div class="p-recruit__form--item">
      <p class="item-name">
        お名前
        <span class="required">必須</span>
      </p>
      <div class="p-4">
        <FormAutokanaInput
          :required="true"
          :required_kana="true"
          v-model:name="recruitData.fullname"
          v-model:kana="recruitData.furigana"
        />
      </div>
    </div>
    <div
      v-if="type == 'newgrad' || type == 'career'"
      class="p-recruit__form--item"
    >
      <p class="item-name">性別<span class="required">必須</span></p>
      <div class="p-4">
        <FormRadioSelect
          class="grid grid-cols-1 tablet:grid-cols-2 gap-2"
          v-model="recruitData.gender"
          :list="genderList"
          name="gender"
        />
      </div>
    </div>
    <div v-if="type == 'career'" class="p-recruit__form--item">
      <p class="item-name">
        年齢
        <span class="required">必須</span>
      </p>
      <div class="p-4">
        <FormInput
          v-model="recruitData.age"
          placeholder="年齢を入力"
          label="年齢"
          alert="年齢をご入力ください"
          :required="true"
        />
      </div>
    </div>
    <div v-if="type == 'newgrad'" class="p-recruit__form--item">
      <p class="item-name">卒業(予定)年月<span class="required">必須</span></p>
      <div class="p-4">
        <FormDateInput
          v-model="recruitData.graduate"
          placeholder="卒業(予定)年月を入力(ハイフン自動)"
          label="卒業(予定)年月"
          label2="例:2000-03"
          format="ym"
          alert="卒業(予定)年月をご入力ください"
          :required="true"
        />
      </div>
    </div>
    <div v-if="type == 'newgrad'" class="p-recruit__form--item">
      <p class="item-name">
        所属している学校名
        <span class="required">必須</span>
      </p>
      <div class="p-4">
        <FormInput
          v-model="recruitData.school"
          placeholder="学校名を入力"
          label="学校名"
          alert="所属している学校名を入力してください"
          :required="true"
        />
      </div>
    </div>
    <div class="p-recruit__form--item">
      <p class="item-name">
        住所
        <span class="required">必須</span>
      </p>

      <div class="p-4">
        <FormAddressInput
          :required="true"
          v-model:postnum="recruitData.post"
          v-model:address="recruitData.address"
        ></FormAddressInput>
      </div>
    </div>
    <div v-if="type == 'contractor'" class="p-recruit__form--item">
      <p class="item-name">
        会社/事業者名
        <span class="required">必須</span>
      </p>
      <div class="p-4">
        <FormInput
          v-model="recruitData.business_name"
          placeholder="会社名/事業者名を入力"
          label="会社名/事業者名"
          alert="会社名/事業者名をご入力ください"
          :required="true"
        />
      </div>
    </div>
    <div class="p-recruit__form--item">
      <p class="item-name">
        電話番号
        <span class="required">必須</span>
      </p>
      <div class="p-4">
        <FormTelephoneInput
          :required="true"
          v-model="recruitData.tel"
        ></FormTelephoneInput>
      </div>
    </div>
    <div class="p-recruit__form--item">
      <p class="item-name">
        メールアドレス
        <span class="required">必須</span>
      </p>
      <div class="p-4">
        <FormMailInput
          :required="true"
          v-model="recruitData.mail"
        ></FormMailInput>
      </div>
    </div>
    <template v-if="type == 'career'">
      <h2 class="p-recruit__form--h1">職務経歴</h2>
      <div class="p-recruit__form--item">
        <p class="item-name">
          直近の在籍会社名
          <span class="required">必須</span>
        </p>
        <div class="p-4">
          <FormInput
            v-model="recruitData.career"
            placeholder="会社名を入力"
            label="会社名"
            alert="会社名をご入力ください"
            :required="true"
          />
        </div>
      </div>
      <div class="p-recruit__form--item">
        <p class="item-name">
          在籍期間
          <span class="required">必須</span>
        </p>
        <div class="p-4">
          <FormInput
            v-model="recruitData.term"
            placeholder="在籍期間を入力"
            label="在籍期間"
            alert="在籍期間をご入力ください"
            :required="true"
          />
        </div>
      </div>
      <div class="p-recruit__form--item">
        <p class="item-name">
          前職業務内容
          <span class="required">必須</span>
        </p>
        <div class="p-4">
          <FormInput
            v-model="recruitData.duties"
            placeholder="前職業務内容を入力"
            label="前職業務内容"
            alert="前職業務内容をご入力ください"
            :required="true"
          />
        </div>
      </div>
      <div class="p-recruit__form--item">
        <p class="item-name">入社可能時期<span class="required">必須</span></p>
        <div class="p-4">
          <FormDateInput
            v-model="recruitData.start"
            placeholder="入社可能時期を入力(ハイフン自動)"
            label="入社可能時期"
            label2="例:2000-03"
            format="ym"
            alert="入社可能時期をご入力ください"
            :required="true"
          />
        </div>
      </div>
    </template>
    <div class="p-recruit__form--item">
      <label for="textarea-message" class="item-name">ご質問・ご要望など</label>
      <div class="p-4">
        <textarea
          id="textarea-message"
          class="w-full border"
          rows="4"
          v-model="recruitData.message"
        />
      </div>
    </div>
    <FormAgreement :required="true" v-model="isAgree">
      <NuxtLink to="/contents.php/hourei" target="_blank" class="mb-4"
        >【個人情報の取扱いについて】にご同意の上、お申込みください</NuxtLink
      >
    </FormAgreement>
    <button class="p-recruit__form--btn">入力内容確認画面</button>
  </div>
</template>
<style scoped lang="scss">
.p-recruit__form {
  @apply mx-auto;
  &--item {
    @apply border border-gray-300 shadow-md relative mb-8 pb-2;
    .required {
      @apply p-1 px-4 bg-red-600 text-white text-sm ml-5;
    }
    .item-name {
      @apply bg-gray-200 p-4 pl-6 text-lg tracking-widest flex flex-wrap;
    }
    .item-content {
      @apply py-4 pc:px-6 px-2;
      label {
        @apply w-full my-2 flex items-center;
        &.half {
          @apply w-1/2;
        }
        textarea {
          @apply border p-2 w-full h-36;
        }
        input {
          @apply mr-2;
          &[type="text"] {
            @apply border p-2 w-full tablet:w-1/2;
            &.full {
              @apply tablet:w-full;
            }
          }
        }
        select {
          @apply p-2;
        }
        .addressSearch-btn {
          @apply bg-gray-400 whitespace-nowrap rounded p-2 px-5 text-white;
        }
      }
    }
  }
  &--h1 {
    @apply text-white bg-gray-500 shadow-md p-4 px-5 text-xl mt-10;
  }
  &--btn {
    @apply m-auto mt-10 mb-14 p-3 px-10 block w-max text-center text-white bg-famitei-green text-lg shadow-md rounded;
  }
}
</style>
